import { assign, fromPromise, sendParent, sendTo, setup } from "xstate";

import TimeRange from "@/types/time-range";

import Layer from "./layer";
import { LayerSettingValue, LayerSettingValueSet } from "./layer-settings";

export type LayerStateMachineContext = {
  isEnabledInitially: boolean;
  layer: Layer;
  timeRange: TimeRange;
  currentSettingValues: LayerSettingValueSet;
};

export type LayerConfig = {
  layer: Layer;
  isEnabledInitially: boolean;
};

export type LayerStateMachineInput = {
  layerConfig: LayerConfig;
  timeRange: TimeRange;
};

export type LayerStateMachineEvent =
  | { type: "TOGGLE" }
  | { type: "STARTED_LOADING" }
  | { type: "STARTED_LOADING_CURRENT_VIEW" }
  | { type: "COMPLETED_LOADING" }
  | { type: "SET_TIME_RANGE"; timeRange: TimeRange }
  | {
      type: "SET_SETTING_VALUE";
      settingId: string;
      settingValue: LayerSettingValue<unknown>;
    }
  | { type: "REINITIALIZE" }
  | { type: "REFRESH" }
  | { type: "ENABLE" }
  | { type: "DISABLE" }
  | { type: "FAIL" };

export const layerStateMachine = setup({
  types: {
    context: {} as LayerStateMachineContext,
    input: {} as LayerStateMachineInput,
    events: {} as LayerStateMachineEvent,
  },
  actors: {
    initialize: fromPromise<void, { layer: Layer; timeRange: TimeRange }>(
      async ({ input }) => {
        await input.layer.initialize(input.timeRange);
      },
    ),
  },
}).createMachine({
  id: "layer",
  context: ({ input, self }) => {
    const layer = input.layerConfig.layer;

    layer.loadingManager?.onStart(() => {
      self.send({ type: "STARTED_LOADING" });
    });
    layer.loadingManager?.onComplete(() => {
      self.send({ type: "COMPLETED_LOADING" });
    });
    layer.currentViewLoadingManager?.onStart(() => {
      self.send({ type: "STARTED_LOADING_CURRENT_VIEW" });
    });

    const currentSettingValues = {} as LayerSettingValueSet;
    layer.settings.forEach((setting) => {
      currentSettingValues[setting.id] = setting.default;
    });

    return {
      ...input.layerConfig,
      timeRange: input.timeRange,
      currentSettingValues,
    };
  },

  initial: "initial",
  states: {
    initial: {
      always: [
        { target: "enabled", guard: ({ context }) => context.isEnabledInitially },
        { target: "disabled", guard: ({ context }) => !context.isEnabledInitially },
      ],
    },
    disabled: {
      entry: sendParent({
        type: "LAYER_DISABLED",
      }),
      on: {
        TOGGLE: "enabled",
      },
    },
    enabled: {
      initial: "initializing",
      states: {
        initializing: {
          invoke: {
            input: ({ context }) => ({
              layer: context.layer,
              timeRange: context.timeRange,
            }),
            src: "initialize",
            onDone: {
              target: "ready",
            },
            onError: {
              target: "error",
            },
          },
          entry: sendParent({ type: "LAYER_STARTED_INITIALIZING" }),
          exit: sendParent({ type: "LAYER_COMPLETED_INITIALIZING" }),
        },
        error: {
          on: {
            REINITIALIZE: "initializing",
          },
        },
        ready: {
          initial: "idle",
          on: {
            REINITIALIZE: ".reinitializing",
          },
          states: {
            idle: {
              on: {
                STARTED_LOADING: "loading",
                STARTED_LOADING_CURRENT_VIEW: {
                  target: "loading",
                  actions: sendParent({
                    type: "LAYER_REQUIRES_ADDITONAL_DATA",
                  }),
                },
              },
            },
            reinitializing: {
              invoke: {
                input: ({ context }) => ({
                  layer: context.layer,
                  timeRange: context.timeRange,
                }),
                src: "initialize",
                onDone: {
                  target: "idle",
                },
                onError: {
                  target: "#layer.enabled.error",
                },
              },
              entry: sendParent({ type: "LAYER_STARTED_INITIALIZING" }),
              exit: sendParent({ type: "LAYER_COMPLETED_INITIALIZING" }),
            },
            loading: {
              on: {
                STARTED_LOADING_CURRENT_VIEW: {
                  actions: sendParent({
                    type: "LAYER_REQUIRES_ADDITONAL_DATA",
                  }),
                },
                COMPLETED_LOADING: {
                  target: "idle",
                },
              },
              entry: sendParent({ type: "LAYER_STARTED_LOADING" }),
              exit: sendParent({ type: "LAYER_COMPLETED_LOADING" }),
            },
          },
        },
      },
      on: {
        FAIL: ".error",
        TOGGLE: {
          target: "disabled",
        },
      },
    },
  },
  on: {
    SET_TIME_RANGE: {
      actions: [
        assign({ timeRange: ({ event }) => event.timeRange }),
        sendTo(({ self }) => self, { type: "REINITIALIZE" }),
      ],
    },
    SET_SETTING_VALUE: {
      actions: [
        assign({
          currentSettingValues: ({ event, context }) => {
            return {
              ...context.currentSettingValues,
              [event.settingId]: event.settingValue,
            };
          },
        }),
      ],
    },
    REFRESH: {
      actions: sendTo(({ self }) => self, { type: "REINITIALIZE" }),
    },
    DISABLE: {
      target: ".disabled",
    },
    ENABLE: {
      target: ".enabled",
    },
  },
});
